<script setup>

    import { computed, ref, watch } from 'vue'
    import { storeToRefs } from 'pinia'

    import { useClientSingleStore, useConfigStore, useViewStore } from '@/Stores'
    import { IconFormNew28Regular, IconPersonInfo16Regular, IconPersonTag20Regular } from '@iconify-prerendered/vue-fluent'
    import Modal from '@/Components/App/Modal.vue'
    import Loader from '@/Components/App/Loader.vue'
    import LatteClientCareNeedSelection from './LatteClientCareNeedSelection.vue'
    import LatteClientVitalModal from '@/Components/Client/LatteClientVitalModal.vue'
    import { CARE_RECIPIENT_CONTACT_TYPE, getModifiedProperties, handleBlur, inchesToFeetAndInches } from '@/Helpers'
    import { hideAllPoppers } from 'floating-vue'

    const emit = defineEmits(['client:update', 'client:cancel-update'])

    const clientSingleStore = useClientSingleStore()
    const configStore = useConfigStore()
    const viewStore = useViewStore()
    const { isEmbedded } = storeToRefs(viewStore)
    const { config, criteriaNameDict, clientCriterias } = storeToRefs(configStore)
    const { client, loading } = storeToRefs(clientSingleStore)

    const localClient = ref({
        height: {
        },
        weight: null,
        gender: null,
        matching_criterias: []
    })

    const genders = [
        {
            id: "male",
            name: "Male"
        },
        {
            id: "female",
            name: "Female"
        }
    ]

    const clientPropsChange = ref(false)

    const showSelectionModal = ref(false)
    const selectionModalRef = ref(null)

    const showVitalModal = ref(false)


    watch(() => client.value, (value) => {
        localClient.value = JSON.parse(JSON.stringify(value))
        localClient.value.height = inchesToFeetAndInches(value.height)

        const clientContact = value.contacts?.find(contact => contact.type === CARE_RECIPIENT_CONTACT_TYPE)
        if (clientContact) {
            localClient.value.gender = genders.find(g => g.id === clientContact.gender) || null
        } else {
            localClient.value.gender = null
        }

        if (localClient.value.id) {
            clientPropsChange.value = true
        }
    }, { deep: true, immediate: true })


    function extractClient(proxyClient) {
        let height = NaN
        if (proxyClient.height.foot) {
            height = parseInt(proxyClient.height.foot) * 12
        }

        if (proxyClient.height.inch) {
            height = isNaN(height) ? 0 : height
            height += parseInt(proxyClient.height.inch)
        }

        let gender = null
        if (proxyClient.gender) {
            gender = proxyClient.gender.id
        }

        return {
            ...proxyClient,
            height: height,
            gender: gender,
        };
    }

    watch(localClient, (newValue, oldValue) => {
        if (oldValue === undefined) {
            clientPropsChange.value = false;
            return;
        }

        if (oldValue.id === newValue.id) {
            if(clientPropsChange.value == false) {
                const diff = getModifiedProperties(
                    JSON.stringify(extractClient(newValue)),
                    JSON.stringify(client.value),
                    [
                        'height', 'weight', 'gender', 'matching_criterias'
                    ]
                )
                handleChange(diff)
                return;
            }
        }
        clientPropsChange.value = false
    }, { immediate: true, deep: true })

    const handleChange = (data) => {
        if (Object.keys(data).length > 0) {
            emit('client:update', data);
        } else {
            emit('client:cancel-update', client.value.id);
        }
    }

    const selectedMedicalChartables = computed(() => {
        return localClient.value.matching_criterias.filter((criteria) => {
            if (!criteriaNameDict.value[criteria.name_id])
                return false;
            if (criteriaNameDict.value[criteria.name_id].is_medical_chartable) {
                return true;
            }
            return false;
        })
    })

    const selectedMustHaves = computed(() => {
        return localClient.value.matching_criterias.filter((criteria) => {
            return criteria.factor === 'must_have';
        })
    })

    const selectedWows = computed(() => {
        return localClient.value.matching_criterias.filter((criteria) => {
            return criteria.factor === 'wow';
        })
    })

    const selectedSummary = computed(() => {
        return {
            chartable : selectedMedicalChartables.value.length,
            must_have : selectedMustHaves.value.length,
            wow : selectedWows.value.length
        };
    })

    function getCriteriaPresentationPillText(criteria, withoutExp = false) {
        if (!criteriaNameDict.value[criteria.name_id]) {
            return '';
        }
        let output;
        let presentation = criteriaNameDict.value[criteria.name_id].presentation;
        if (presentation.show === 'label') {
            output = criteriaNameDict.value[criteria.name_id].label
        } else if (presentation.show === 'attributes') {
            output = criteria.attributes
                .filter(attribute => attribute.selected_choice_ids.length > 0)
                .map(attribute => {
                    const selectedChoices = attribute.selected_choice_ids.map(choice_id => {
                        return criteriaNameDict.value[criteria.name_id].attribute_dict[attribute.name_id].choices.find(choice => choice.id === choice_id).label;
                    });
                    return selectedChoices.join(presentation.concat_between);
                }).join(presentation.concat_between);
        } else {
            return criteriaNameDict.value[criteria.name_id].label;
        }
        if (presentation.concat_start) {
            output = presentation.concat_start + ' ' + output;
        }
        if (presentation.concat_end) {
            output = output + ' ' + presentation.concat_end;
        }
        if (withoutExp) {
            let expVariants = [ 'Exp.', 'Experience' ]
            expVariants.forEach((exp) => {
                output = output.replace(exp, '').trim()
            })
        }
        return output;
    }

    function toggleSelectionModal(scrollToNameId = null) {
        showSelectionModal.value = !showSelectionModal.value
        if (scrollToNameId) {
            setTimeout(() => {
                selectionModalRef.value.scrollToCriteria(scrollToNameId)
            }, 50)
        }
    }

</script>

<template>

    <div>

        <div
            id="client_care_need"
            class="p-4 pt-3 pb-5"
        >
            <div class="grid grid-cols-7 gap-6">
                <div class="col-span-2">
                    <div class="flex flex-col gap-3 h-full">


                        <div class="pb-1">
                            <span class="font-bold text-sm text-lifeworx-blue-800 pb-1 inline-block">Vital Statistics</span>

                            <div
                                @click="() => !loading && (showVitalModal = true)"
                            >
                                <div
                                    v-if="!loading && !(localClient.gender || localClient.height.foot || localClient.height.inch || localClient.weight)"
                                    class="flex flex-col h-[90px] items-center border border-dashed rounded bg-neutral-50 py-2 text-center cursor-pointer hover:bg-lifeworx-blue-600/10 hover:border-lifeworx-blue-600/30"
                                >
                                    <IconPersonInfo16Regular class="h-9 w-9 mt-2 mb-1 text-lifeworx-blue-800/30" />
                                    <span class="mb-2 text-lifeworx-blue-800 font-semibold tracking-wide text-xs">Add Vital Statistics</span>
                                </div>
                                <div
                                    v-else-if="loading"
                                    class="flex h-[90px] items-center border border-dashed rounded bg-neutral-50 py-2"
                                >
                                    <Loader class="scale-75" />
                                </div>
                                <div
                                    v-else
                                    class="flex flex-col items-center border border-dashed rounded bg-neutral-50 py-8 text-center cursor-pointer hover:bg-lifeworx-blue-600/10 hover:border-lifeworx-blue-600/30"
                                >
                                    <div class="grid grid-cols-3 w-full px-4 gap-4">
                                        <div class="flex flex-col items-center">
                                            <span class="text-lg font-semibold text-lifeworx-blue-600 border-b border-dashed border-lifeworx-blue-600/50 leading-5">
                                                {{ localClient.gender?.name || '—' }}
                                            </span>
                                        </div>
                                        <div class="flex flex-col items-center">
                                            <span class="text-lg font-semibold text-lifeworx-blue-600 border-b border-dashed border-lifeworx-blue-600/50 leading-5">
                                                {{ localClient.height?.foot || '—' }}' {{ localClient.height?.inch || '—' }}"
                                            </span>
                                        </div>
                                        <div class="flex flex-col items-center">
                                            <span class="text-lg font-semibold text-lifeworx-blue-600 border-b border-dashed border-lifeworx-blue-600/50 leading-5">
                                                {{ localClient.weight || '—' }}<span class="text-sm">lb</span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>


                        </div>


                        <div class="flex-1 flex flex-col">
                            <span class="font-bold text-xs text-lifeworx-blue-800 pb-1 inline-block">Health Conditions &amp; Medical Devices</span>

                            <div
                                @click.stop="() => !loading && toggleSelectionModal()"
                                :class="[
                                    'flex h-[196px] border border-dashed rounded bg-neutral-50',
                                    {
                                        'items-center justify-center' : selectedMedicalChartables.length == 0 || loading,
                                        'items-start justify-start' : selectedMedicalChartables.length > 0,
                                        'hover:bg-lifeworx-blue-600/10 hover:border-lifeworx-blue-600/30 text-center cursor-pointer' : !loading
                                    }
                                ]"
                            >
                                <div v-if="!loading && selectedMedicalChartables.length == 0"
                                    class="flex flex-col items-center"
                                >
                                    <IconFormNew28Regular class="h-10 w-10 mb-1.5 text-lifeworx-blue-800/30" />
                                    <span class="text-lifeworx-blue-800 font-semibold tracking-wide text-xs">Add Health Conditions</span>
                                </div>
                                <div
                                    v-else-if="loading"
                                    class="flex flex-col items-center"
                                >
                                    <Loader class="scale-75" />
                                </div>
                                <ul
                                    v-else
                                    class="p-3 w-full h-full overflow-y-scroll"
                                >
                                    <template
                                        v-for="criteria in selectedMedicalChartables"
                                        :key="criteria.name_id"
                                    >
                                        <li
                                            v-if="getCriteriaPresentationPillText(criteria, true).length > 0"
                                            @click.stop="() => toggleSelectionModal(criteria.name_id)"
                                            class="float-left mr-2 mb-2 text-xs inline-block border border-lifeworx-red-500 bg-[#f8e0d9] font-semibold text-lifeworx-red-500 p-1 px-2 rounded-full opacity-90 border-opacity-50 hover:opacity-100 hover:border-opacity-100"
                                        >
                                            {{ getCriteriaPresentationPillText(criteria, true) }}
                                            <span
                                                v-if="criteria.factor == 'must_have'"
                                                class="rounded-full inline-block bg-lifeworx-orange-500 h-2 w-2 ml-0.5"
                                            />
                                            <span
                                                v-if="criteria.factor == 'wow'"
                                                class="rounded-full inline-block bg-lifeworx-purple-400 h-2 w-2 ml-0.5"
                                            />
                                        </li>
                                    </template>
                                </ul>
                            </div>

                        </div>

                    </div>
                </div>

                <div class="col-span-5">
                    <div class="grid grid-cols-2">
                        <div class="col-span-1">
                            <span class="font-bold text-sm text-lifeworx-blue-800 pb-1 inline-block">Must-Haves</span>
                        </div>
                        <div class="col-span-1">
                            <span class="font-bold text-sm text-lifeworx-blue-800 pb-1 inline-block">Wow-Factors</span>
                        </div>
                    </div>

                    <div
                        @click.stop="() => !loading && toggleSelectionModal()"
                        :class="[
                            'relative border border-dashed rounded bg-neutral-50 group',
                            {
                                'grid grid-cols-2 cursor-pointer hover:bg-lifeworx-blue-600/10 hover:border-lifeworx-blue-600/30' : !loading
                            }
                        ]"
                    >
                        <div
                            v-if="!loading && selectedMustHaves.length == 0 && selectedWows.length == 0"
                            class="absolute inset-0 my-3 flex items-center justify-center bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-neutral-50 to-transparent"
                        >
                            <div
                                class="flex flex-col items-center"
                            >
                                <IconPersonTag20Regular class="h-12 w-12 mb-2 text-lifeworx-blue-800/30" />
                                <span class="pb-2 text-lifeworx-blue-800 font-semibold tracking-wide text-xs">Add Caregiver Criteria</span>
                            </div>
                        </div>
                        <div
                            v-else-if="loading"
                            @scroll="hideAllPoppers()"
                            class="h-[324px] flex items-center justify-center bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-neutral-50 to-transparent"
                        >
                            <div
                                class="flex flex-grow justify-center items-center"
                            >
                                <Loader />
                            </div>
                        </div>
                        <div
                            v-if="!loading"
                            class="h-[324px] p-3 overflow-y-scroll"
                        >
                            <ul
                                v-if="selectedMustHaves.length > 0"
                                class="inline-block w-full"
                            >
                                <template
                                    v-for="criteria in selectedMustHaves"
                                    :key="criteria.name_id"
                                >
                                    <li
                                        v-if="getCriteriaPresentationPillText(criteria).length > 0"
                                        @click.stop="() => toggleSelectionModal(criteria.name_id)"
                                        class="float-left h-[26px] mr-2 mb-2 text-xs inline-block border border-lifeworx-orange-600 bg-[#f4e7d9] font-semibold text-lifeworx-orange-600 rounded-full max-w-full opacity-90 border-opacity-50 hover:opacity-100 hover:border-opacity-100"
                                    >
                                        <VTooltip
                                            v-if="getCriteriaPresentationPillText(criteria).length > 45"
                                            :triggers="['hover']"
                                            :autoHide="true"
                                            @apply-hide="handleBlur"
                                        >
                                            <span class="w-full mt-1 h-5 px-2 inline-block truncate whitespace-nowrap overflow-ellipsis overflow-hidden text-nowrap">
                                                {{ getCriteriaPresentationPillText(criteria) }}
                                            </span>

                                            <template #popper>
                                                <span class="text-sm">
                                                    {{ getCriteriaPresentationPillText(criteria) }}
                                                </span>
                                            </template>

                                        </VTooltip>
                                        <span
                                            v-else
                                            class="w-full mt-1 h-5 px-2 inline-block truncate whitespace-nowrap overflow-ellipsis overflow-hidden text-nowrap"
                                        >
                                            {{ getCriteriaPresentationPillText(criteria) }}
                                        </span>
                                    </li>
                                </template>
                            </ul>
                        </div>
                        <div
                            v-if="!loading"
                            @scroll="hideAllPoppers()"
                            :class="[
                                'h-[324px] p-3 pb-0.5 overflow-y-scroll border-l border-dashed group-hover:border-lifeworx-blue-600/30'
                            ]"
                        >
                            <ul
                                v-if="selectedWows.length > 0"
                                class="inline-block w-full"
                            >
                                <template
                                    v-for="criteria in selectedWows"
                                    :key="criteria.name_id"
                                >
                                    <li
                                        v-if="getCriteriaPresentationPillText(criteria).length > 0"
                                        @click.stop="() => toggleSelectionModal(criteria.name_id)"
                                        class="float-left h-[26px] mr-2 mb-2 text-xs inline-block border border-lifeworx-purple-400 bg-[#efddfa] font-semibold text-lifeworx-purple-400 rounded-full max-w-full opacity-90 border-opacity-50 hover:opacity-100 hover:border-opacity-100"
                                    >
                                        <VTooltip
                                            v-if="getCriteriaPresentationPillText(criteria).length > 45"
                                            :triggers="['hover']"
                                            :autoHide="true"
                                            @apply-hide="handleBlur"
                                        >
                                            <span class="w-full mt-1 h-5 px-2 inline-block truncate whitespace-nowrap overflow-ellipsis overflow-hidden text-nowrap">
                                                {{ getCriteriaPresentationPillText(criteria) }}
                                            </span>

                                            <template #popper>
                                                <span class="text-sm">
                                                    {{ getCriteriaPresentationPillText(criteria) }}
                                                </span>
                                            </template>

                                        </VTooltip>
                                        <span
                                            v-else
                                            class="w-full mt-1 h-5 px-2 inline-block truncate whitespace-nowrap overflow-ellipsis overflow-hidden text-nowrap"
                                        >
                                            {{ getCriteriaPresentationPillText(criteria) }}
                                        </span>
                                    </li>
                                </template>
                            </ul>
                        </div>
                    </div>

                </div>
            </div>

        </div>

        <div class="bg-white flex justify-between border-t border-stone-300 p-6">
            <div class="grid content-center">
                <span
                    v-if="loading"
                    class="inline-block h-full text-sm font-semibold italic"
                >Loading...</span>
                <span
                    v-else-if="!loading && selectedSummary.chartable || selectedSummary.must_have || selectedSummary.wow"
                    class="inline-block h-full text-sm"
                >
                    Selected
                    <span
                        v-if="selectedSummary.chartable"
                        class="text-lifeworx-red-500 font-bold"
                    >
                        {{ selectedSummary.chartable }}
                        {{ selectedSummary.chartable > 1 ? 'conditions/devices' : 'condition/device'}}
                    </span>
                    <template
                        v-if="selectedSummary.chartable && selectedSummary.must_have && selectedSummary.wow"
                    >, </template>
                    <template
                        v-if="selectedSummary.chartable && selectedSummary.must_have && !selectedSummary.wow"
                    > and </template>
                    <span
                        v-if="selectedSummary.must_have" class="text-lifeworx-orange-500 font-bold"
                    >
                        {{ selectedSummary.must_have }}
                        {{ selectedSummary.must_have > 1 ? 'must-haves' : 'must-have'}}
                    </span>
                    <template
                        v-if="selectedSummary.chartable && selectedSummary.must_have && selectedSummary.wow"
                    >, and </template>
                    <template
                        v-if="!selectedSummary.chartable && selectedSummary.must_have && selectedSummary.wow"
                    > and </template>
                    <span
                        v-if="selectedSummary.wow"
                        class="text-lifeworx-purple-400 font-bold"
                    >
                        {{ selectedSummary.wow }}
                        {{ selectedSummary.wow > 1 ? 'wow-factors' : 'wow-factor'}}
                    </span>
                </span>
                <span
                    v-else-if="loading"
                    class="inline-block h-full text-sm font-bold italic"
                >Loading...</span>
                <span
                    v-else
                    class="inline-block h-full text-sm"
                >
                    <span class="font-bold">Tip:</span> Get started by adding client health conditions, must-haves, and wow-factors.
                </span>
            </div>
        </div>

        <Modal
            :show="showVitalModal"
            maxWidth="3xl"
            :padding-enable="false"
            :verticalOffset="isEmbedded ? -100 : null"
        >
            <LatteClientVitalModal
                :client="localClient"
                @close="showVitalModal = false"
            />
        </Modal>

        <Modal
            :show="showSelectionModal"
            :padding-enable="false"
            max-width="4xl"
            :verticalOffset="isEmbedded ? -100 : null"
        >
            <LatteClientCareNeedSelection
                ref="selectionModalRef"
                v-model="localClient.matching_criterias"
                :categories="config.matching_criteria_descriptor.categories"
                :criterias="clientCriterias"
                @close="toggleSelectionModal"
            />
        </Modal>

    </div>

</template>
